import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "../api";
import Layout from "../components/Layout";
import Swal from "sweetalert2";
import RegisterAttemptModal from "../components/RegisterAttemptModal";
import HistoryModal from "../components/HistoryModal";
import { getStepLabel } from "../utils/follow-up-steps";
import { onDataChanged } from "../services/socketService";

export default function CoordinatorKanbanStep() {

  const { step } = useParams();
  const navigate = useNavigate();

  const [followUps, setFollowUps] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedFollowUp, setSelectedFollowUp] = useState(null);
  const [viewHistory, setViewHistory] = useState(null);
  const [viewAbsences, setViewAbsences] = useState(null);

  const fetchData = useCallback(async (pageNumber) => {
    const response = await api.get(
      `/followups?step=${step}&page=${pageNumber}&limit=20`
    );

    setFollowUps(response.data.data || []);
    setPage(response.data.page);
    setTotalPages(response.data.totalPages);
  }, [step]);

  useEffect(() => {
    fetchData(1);
  }, [fetchData]);

  useEffect(() => {
    const unsubscribe = onDataChanged(() => {
      fetchData(page);
    });

    return () => unsubscribe();
  }, [page, fetchData]);

  async function advance(id) {
    await api.put(`/followups/advance/${id}`);
    fetchData(page);
  }

  async function resolve(id) {
    await api.put(`/followups/resolve/${id}`, {
      resolvedBy: "COORDINATION",
    });
    fetchData(page);
  }

  async function confirmEvasion(id) {
    await api.put(`/followups/confirm/${id}`);
    fetchData(page);
  }

  async function regress(id) {

  const confirmAction = await Swal.fire({
    title: "Retornar etapa?",
    icon: "question",
    showCancelButton: true
  });

  if (!confirmAction.isConfirmed) return;

  await api.put(`/followups/regress/${id}`);
  fetchData(page);
}


  // 🔥 WHATSAPP DINÂMICO
  function openWhatsApp(student) {

    let phone = null;
    let message = "";

    if (step === "STEP_1_STUDENT_WPP") {
      phone = student?.phone;
      message =
        `Ola ${student?.name || ""}, tudo bem? ` +
        "Aqui e da equipe pedagogica. Notamos faltas recentes e queremos entender " +
        "se podemos te apoiar para retomar a frequencia nas aulas.";
    }

    if (step === "STEP_2_RESPONSIBLE_WPP") {
      phone = student?.responsibles?.[0]?.phone;
      message =
        `Ola! Aqui e da equipe pedagogica, sobre o(a) aluno(a) ${student?.name || ""}. ` +
        "Notamos faltas recentes e gostaríamos de alinhar como podemos ajudar no retorno e continuidade.";
    }

    if (!phone) {
      Swal.fire("Telefone não encontrado");
      return;
    }

    const cleanPhone = phone.replace(/\D/g, "");

    const encoded = encodeURIComponent(message);

    window.open(`https://wa.me/55${cleanPhone}?text=${encoded}`, "_blank");
  }

  return (
    <Layout>

      {/* Topo */}
      <div className="d-flex justify-content-between align-items-center mb-4">

        <h4>
          Etapa: {getStepLabel(step)}
        </h4>

        <button
          className="btn btn-outline-secondary"
          onClick={() => navigate("/coordinator")}
        >
          ← Voltar para Dashboard
        </button>

      </div>

      {/* Cards */}
      <div className="row">

        {followUps.map(followUp => {

          const student = followUp?.student;
          if (!student) return null;

          const stepsOrder = [
          "STEP_1_STUDENT_WPP",
          "STEP_2_RESPONSIBLE_WPP",
          "STEP_3_SCHOOL_CONTACT",
          "STEP_4_HOME_VISIT"
          ];

          const currentIndex = stepsOrder.indexOf(followUp.currentStep);
          const canGoBack = currentIndex > 0;
          const canAdvance = currentIndex >= 0 && currentIndex < stepsOrder.length - 1;

          return (
            <div key={followUp.id} className="col-md-4 mb-3">

              <div className="card shadow-sm h-100">

                <div className="card-body">

                  <h6 className="fw-bold">
                    {student.name}
                  </h6>

                  <small className="text-muted d-block">
                    Turma: {student.class?.name || "N/A"}
                  </small>

                  <div className="mt-3 d-grid gap-2">

                    {/* 🔥 WHATSAPP */}
                    {(step === "STEP_1_STUDENT_WPP" ||
                      step === "STEP_2_RESPONSIBLE_WPP") && (

                      <button
                        className="btn btn-sm btn-success"
                        onClick={() => openWhatsApp(student)}
                      >
                        💬 Abrir WhatsApp
                      </button>
                    )}

                    {/* 👁 VER FALTAS */}
                    <button
                      className="btn btn-sm btn-outline-warning"
                      onClick={() => setViewAbsences(student)}
                    >
                      📅 Ver Faltas
                    </button>

                    <button
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => setSelectedFollowUp(followUp)}
                    >
                      Registrar Tentativa
                    </button>

                    <button
                      className="btn btn-sm btn-outline-dark"
                      onClick={() => setViewHistory(followUp)}
                    >
                      Ver Histórico
                    </button>

                    <button
                      className="btn btn-sm btn-outline-warning"
                      disabled={!canGoBack}
                      onClick={() => regress(followUp.id)}
                      >
                      ⬅ Retornar Etapa
                    </button>


                    <button
                      className="btn btn-sm btn-outline-primary"
                      disabled={!canAdvance}
                      onClick={() => advance(followUp.id)}
                    >
                      Avançar Etapa
                    </button>

                    <button
                      className="btn btn-sm btn-success"
                      onClick={() => resolve(followUp.id)}
                    >
                      Resolver
                    </button>

                    <button
                      className="btn btn-sm btn-danger"
                      onClick={() => confirmEvasion(followUp.id)}
                    >
                      Confirmar Evasão
                    </button>

                  </div>

                </div>

              </div>

            </div>
          );
        })}

      </div>

      {/* Paginação */}
      <div className="d-flex justify-content-center align-items-center mt-4">

        <button
          className="btn btn-outline-secondary me-3"
          disabled={page === 1}
          onClick={() => fetchData(page - 1)}
        >
          Anterior
        </button>

        <span>
          Página {page} de {totalPages}
        </span>

        <button
          className="btn btn-outline-secondary ms-3"
          disabled={page === totalPages}
          onClick={() => fetchData(page + 1)}
        >
          Próxima
        </button>

      </div>

      {/* MODAIS */}
      {selectedFollowUp && (
        <RegisterAttemptModal
          followUpId={selectedFollowUp.id}
          onClose={() => setSelectedFollowUp(null)}
          onSuccess={() => fetchData(page)}
        />
      )}

      {viewHistory && (
        <HistoryModal
          followUpId={viewHistory.id}
          onClose={() => setViewHistory(null)}
        />
      )}

      {/* 🔥 MODAL FALTAS */}
      {viewAbsences && (
        <div className="custom-modal-overlay">
          <div className="custom-modal">

            <h5>Faltas - {viewAbsences.name}</h5>

            {(viewAbsences.absences ?? []).length === 0 ? (
              <p>Nenhuma falta registrada.</p>
            ) : (
              (viewAbsences.absences ?? []).map(abs => (
                <div key={abs.id} className="border p-2 mb-2 rounded">

                  <strong>
                    {new Date(abs.date).toLocaleDateString()}
                  </strong>

                  <div>
                    {abs.reason || "Sem motivo informado"}
                  </div>

                </div>
              ))
            )}

            <div className="text-end mt-3">
              <button
                className="btn btn-secondary"
                onClick={() => setViewAbsences(null)}
              >
                Fechar
              </button>
            </div>

          </div>
        </div>
      )}

    </Layout>
  );
}


